var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { GenerateUserTokenResult, } from "healthex-common/auth.dtos";
import { checkUserType } from "healthex-common/checkusertype";
import { err, isOk, voidResult } from "healthex-common/result";
import { usaStateFromName } from "healthex-common/usaStates";
import { UserPermissions, UserType } from "healthex-common/user.dtos";
import { getFullName, getJWTPayload, willNeverHappen } from "healthex-common/utils";
import { computed, observable, reaction } from "mobx";
import { SmartAuthType } from "./SmartAuthManager";
import { isDTCOrganization } from "healthex-common/organization.dtos";
var LoginFlow;
(function (LoginFlow) {
    LoginFlow["PASSWORD"] = "PASSWORD";
    LoginFlow["SSO"] = "SSO";
    LoginFlow["SMART_CODE"] = "SMART_CODE";
    LoginFlow["SMART_CODE_REAUTH"] = "SMART_CODE_REAUTH";
    LoginFlow["GFID_CLEAR"] = "GFID_CLEAR";
    LoginFlow["GFID_CLEAR_REAUTH"] = "GFID_CLEAR_REAUTH";
    LoginFlow["GFID_CLEAR_IAL2"] = "GFID_CLEAR_IAL2";
    LoginFlow["BOT_LOGIN"] = "BOT_LOGIN";
})(LoginFlow || (LoginFlow = {}));
export class AuthManager {
    get token() {
        var _a, _b;
        return (_b = (_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.token) !== null && _b !== void 0 ? _b : null;
    }
    get isLoggedIn() {
        return !!this.tokenInfo;
    }
    get tokenPayload() {
        if (!this.tokenInfo)
            return null;
        return getJWTPayload(this.tokenInfo.token);
    }
    get permissions() {
        var _a, _b;
        return (_b = (_a = this.tokenPayload) === null || _a === void 0 ? void 0 : _a.permissions) !== null && _b !== void 0 ? _b : [];
    }
    get isProvider() {
        return this.permissions.includes(UserPermissions.PROVIDER);
    }
    get isEmbeddedProvider() {
        return this.isProvider && !!this.associatedPatientMRN;
    }
    get userType() {
        var _a, _b;
        return (_b = (_a = this.tokenPayload) === null || _a === void 0 ? void 0 : _a.type) !== null && _b !== void 0 ? _b : null;
    }
    get selectedOrganization() {
        var _a, _b, _c, _d;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) !== "PATIENT") {
            return null;
        }
        if (!((_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.selectedPatientOrganizationId) || !((_c = this.tokenPayload) === null || _c === void 0 ? void 0 : _c.authenticatedOrganizations)) {
            console.error(`No selected organization`);
            return null;
        }
        for (const healthcareSystem of this.tokenPayload.authenticatedOrganizations) {
            if (healthcareSystem.organizationId === ((_d = this.tokenInfo) === null || _d === void 0 ? void 0 : _d.selectedPatientOrganizationId)) {
                return healthcareSystem;
            }
        }
        return null;
    }
    get id() {
        var _a, _b;
        if (!this.selectedOrganization) {
            return (_b = (_a = this.tokenPayload) === null || _a === void 0 ? void 0 : _a.sub) !== null && _b !== void 0 ? _b : null;
        }
        return this.selectedOrganization.userId;
    }
    get mrn() {
        if (!this.selectedOrganization || this.selectedOrganization.type !== UserType.HEALTHCARE_ORGANIZATION) {
            return null;
        }
        return this.selectedOrganization.mrn;
    }
    get orgId() {
        var _a, _b;
        if (!this.selectedOrganization) {
            return (_b = (_a = this.tokenPayload) === null || _a === void 0 ? void 0 : _a.organizationId) !== null && _b !== void 0 ? _b : null;
        }
        return this.selectedOrganization.organizationId;
    }
    get name() {
        if (!this.tokenInfo)
            return null;
        return getFullName(this.tokenInfo);
    }
    get firstName() {
        var _a;
        return (_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.firstName;
    }
    get lastName() {
        var _a;
        return (_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.lastName;
    }
    get providerId() {
        var _a;
        return (_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.providerId;
    }
    get state() {
        var _a;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === "PATIENT") {
            return usaStateFromName(this.tokenInfo.state);
        }
        return null;
    }
    get createdAt() {
        var _a;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === "PATIENT") {
            return this.tokenInfo.createdAt;
        }
        return null;
    }
    get email() {
        var _a, _b, _c;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === "SCREENSHOT_USER") {
            return null;
        }
        return (_c = (_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.email) !== null && _c !== void 0 ? _c : null;
    }
    get phone() {
        var _a, _b, _c;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === "SCREENSHOT_USER") {
            return null;
        }
        return (_c = (_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.phone) !== null && _c !== void 0 ? _c : null;
    }
    get formattedPhone() {
        if (!this.phone)
            return null;
        const match = this.phone.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            return `${match[1]}-${match[2]}-${match[3]}`;
        }
        return this.phone;
    }
    get title() {
        var _a, _b, _c, _d;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === "SCREENSHOT_USER" || ((_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.type) === "PATIENT") {
            return null;
        }
        return (_d = (_c = this.tokenInfo) === null || _c === void 0 ? void 0 : _c.title) !== null && _d !== void 0 ? _d : null;
    }
    get age() {
        var _a, _b;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) !== "PATIENT") {
            return null;
        }
        return (_b = this.tokenInfo.age) !== null && _b !== void 0 ? _b : null;
    }
    get isUnderage() {
        if (this.state === null || this.age === null) {
            return false;
        }
        return this.stateLawsService.isUnderage(this.state, this.age);
    }
    get avatarUrl() {
        var _a, _b, _c, _d;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === "PATIENT" || ((_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.type) === "SCREENSHOT_USER") {
            return null;
        }
        return (_d = (_c = this.tokenInfo) === null || _c === void 0 ? void 0 : _c.avatarUrl) !== null && _d !== void 0 ? _d : null;
    }
    get orgLogoUrl() {
        var _a, _b;
        return (_b = (_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.organizationLogoUrl) !== null && _b !== void 0 ? _b : null;
    }
    get orgName() {
        var _a, _b;
        return (_b = (_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.organizationName) !== null && _b !== void 0 ? _b : null;
    }
    get orgState() {
        var _a;
        if (!((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.organizationState)) {
            return null;
        }
        return usaStateFromName(this.tokenInfo.organizationState);
    }
    get onChainAddress() {
        var _a, _b, _c, _d;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === "PATIENT" || ((_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.type) === "SCREENSHOT_USER") {
            return null;
        }
        return (_d = (_c = this.tokenInfo) === null || _c === void 0 ? void 0 : _c.onChainAddress) !== null && _d !== void 0 ? _d : null;
    }
    get orgPrimaryContactName() {
        var _a;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) !== "PATIENT") {
            return null;
        }
        return this.tokenInfo.organizationPrimaryContactName;
    }
    get orgEmail() {
        var _a;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) !== "PATIENT") {
            return null;
        }
        return this.tokenInfo.organizationEmail;
    }
    get orgPhone() {
        var _a;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) !== "PATIENT") {
            return null;
        }
        return this.tokenInfo.organizationPhone;
    }
    get orgPatientPortalLink() {
        var _a;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) !== "PATIENT") {
            return null;
        }
        return this.tokenInfo.organizationPatientPortalLink;
    }
    get orgPatientPortalName() {
        var _a;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) !== "PATIENT") {
            return null;
        }
        return this.tokenInfo.organizationPatientPortalName;
    }
    get patientOrgType() {
        var _a;
        return ((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === UserType.PATIENT ? this.tokenInfo.patientOrganizationType : null;
    }
    get isUserProfileEmpty() {
        return (this.firstName === null || this.lastName === null || (this.permissions.includes(UserPermissions.PROVIDER) && !this.providerId));
    }
    get isDataUser() {
        var _a;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === UserType.PATIENT) {
            return this.tokenInfo.patientOrganizationType === UserType.EXTERNAL_RESEARCHER;
        }
        return this.userType === UserType.EXTERNAL_RESEARCHER;
    }
    get userForAvatar() {
        if (!this.id || !this.firstName || !this.lastName)
            return null;
        return {
            id: this.id,
            firstName: this.firstName,
            lastName: this.lastName,
            avatarUrl: this.avatarUrl,
        };
    }
    get passwordUpdatedAt() {
        if (!this.tokenInfo)
            return null;
        if (!("passwordUpdatedAt" in this.tokenInfo))
            return null;
        return this.tokenInfo.passwordUpdatedAt;
    }
    get onboardingComplete() {
        var _a, _b;
        return (_b = (_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.onboardingComplete) !== null && _b !== void 0 ? _b : null;
    }
    get organizationIsLive() {
        var _a, _b;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === UserType.HEALTHCARE_ORGANIZATION || ((_b = this.tokenInfo) === null || _b === void 0 ? void 0 : _b.type) === UserType.EXTERNAL_RESEARCHER) {
            return this.tokenInfo.organizationIsLive;
        }
        return null;
    }
    get organizationIsDTC() {
        var _a;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === UserType.PATIENT && this.tokenInfo.selectedPatientOrganizationId) {
            return isDTCOrganization(this.tokenInfo.selectedPatientOrganizationId);
        }
        return false;
    }
    get isOrgAdmin() {
        return this.permissions.includes(UserPermissions.ORG_ADMIN);
    }
    get isResearcher() {
        return this.permissions.includes(UserPermissions.RESEARCHER);
    }
    get availablePatientOrganizations() {
        var _a;
        if (((_a = this.tokenPendingPatientOrganizationSelection) === null || _a === void 0 ? void 0 : _a.user.type) !== UserType.PATIENT) {
            return null;
        }
        return this.tokenPendingPatientOrganizationSelection.user.organizations;
    }
    get gfidProvider() {
        var _a;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) !== "PATIENT") {
            return undefined;
        }
        return this.tokenInfo.gfidProvider;
    }
    get proxyAccess() {
        var _a;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) !== "PATIENT") {
            return undefined;
        }
        return this.tokenInfo.proxyAccess;
    }
    get authenticatedWithIAL2() {
        var _a;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) !== "PATIENT") {
            return false;
        }
        return this.tokenInfo.authenticatedWithIAL2 !== false;
    }
    get associatedPatientMRN() {
        var _a;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) !== "HEALTHCARE_ORGANIZATION") {
            return undefined;
        }
        return this.tokenInfo.associatedPatientMRN;
    }
    get consentingPatientInfo() {
        if (this.userType === UserType.PATIENT) {
            if (this.patientOrgType === null || this.id === null || !this.state)
                return null;
            const { id, patientOrgType: orgType, name, isUnderage, state } = this;
            if (orgType === UserType.HEALTHCARE_ORGANIZATION) {
                const { mrn } = this;
                return { id, orgType, mrn, name, isUnderage, state };
            }
            else if (orgType === UserType.EXTERNAL_RESEARCHER) {
                const { firstName, lastName, email, phone } = this;
                return { id, orgType, name, firstName: firstName !== null && firstName !== void 0 ? firstName : null, lastName: lastName !== null && lastName !== void 0 ? lastName : null, email, phone, isUnderage, state };
            }
            else {
                return willNeverHappen(orgType);
            }
        }
        else {
            return null;
        }
    }
    async updateOrganizationIsLive(isLive) {
        var _a;
        if (((_a = this.tokenInfo) === null || _a === void 0 ? void 0 : _a.type) === UserType.HEALTHCARE_ORGANIZATION) {
            if (this.tokenInfo.organizationIsLive === isLive) {
                return;
            }
            this.tokenInfo.organizationIsLive = isLive;
            await this.setTokenInfo(this.tokenInfo);
        }
    }
    constructor(persistence, authService, stateLawsService, embeddedManager, environment) {
        this.persistence = persistence;
        this.authService = authService;
        this.stateLawsService = stateLawsService;
        this.embeddedManager = embeddedManager;
        this.environment = environment;
        this.invalidEmbed = false;
        this.tokenInfo = !this.embeddedManager.embedded ? this.persistence.getTokenInfo() : null;
        this.invalidateTokenInfoIfNeeded();
        reaction(() => this.isLoggedIn, () => {
            if (this.isLoggedIn) {
                void this.refreshTokenInfo();
            }
        }, { fireImmediately: true });
        const checkForExpirationInterval = 20 * 1000;
        setInterval(() => {
            this.scheduleExpirationTimerIfNeeded(checkForExpirationInterval);
        }, checkForExpirationInterval);
        this.scheduleExpirationTimerIfNeeded(checkForExpirationInterval);
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible") {
                this.scheduleExpirationTimerIfNeeded(checkForExpirationInterval);
            }
        });
    }
    invalidateTokenInfoIfNeeded() {
        var _a;
        const tokenExpirationCutoffTimestamp = this.environment.TOKEN_EXPIRATION_CUTOFF_TIMESTAMP_SECONDS;
        if (!tokenExpirationCutoffTimestamp || tokenExpirationCutoffTimestamp.length === 0)
            return;
        if (!this.isLoggedIn || ((_a = this.tokenPayload) === null || _a === void 0 ? void 0 : _a.type) !== UserType.PATIENT)
            return;
        const tokenIssuedAt = this.tokenPayload.iat;
        if (tokenIssuedAt < parseInt(tokenExpirationCutoffTimestamp, 10)) {
            this.logout();
        }
    }
    async authenticateByPassword(email, password) {
        const generateTokenResult = await this.authService.generateTokenByPassword(email, password);
        return await this.handleTokenResult(generateTokenResult, LoginFlow.PASSWORD);
    }
    async getAuthTypeByEmail(email, redirectTo) {
        return await this.authService.getAuthType({ email }, redirectTo);
    }
    async getAuthTypeByOrgId(organizationId, redirectTo) {
        return await this.authService.getAuthType({ organizationId }, redirectTo);
    }
    async authenticateBySmartCode(smartCode, userType, organizationId, redirectUri) {
        const generateTokenResult = await this.authService.generateTokenBySmartCode(smartCode, userType, organizationId, redirectUri);
        return await this.handleTokenResult(generateTokenResult, LoginFlow.SMART_CODE);
    }
    async reauthenticateBySmartCode(smartAuthType, smartCode, organizationId, redirectUri) {
        var _a, _b;
        if (!this.id)
            return err("INCORRECT_CREDENTIALS");
        const generateTokenResult = await this.authService.generateTokenBySmartCode(smartCode, UserType.PATIENT, organizationId, redirectUri);
        if (isOk(generateTokenResult)) {
            if (smartAuthType === SmartAuthType.PROXY_ACCESS_REAUTH) {
                if (this.id === generateTokenResult.user.id &&
                    generateTokenResult.user.type === UserType.PATIENT &&
                    ((_a = generateTokenResult.user.proxyAccess) === null || _a === void 0 ? void 0 : _a.isEnabled) &&
                    ((_b = generateTokenResult.user.proxyAccess) === null || _b === void 0 ? void 0 : _b.relatedPerson)) {
                    return generateTokenResult.user.proxyAccess;
                }
                else {
                    return err("INCORRECT_PROXY_ACCESS");
                }
            }
            else if (this.id !== generateTokenResult.user.id) {
                return err("INCORRECT_CREDENTIALS");
            }
            else {
                return await this.handleTokenResult(generateTokenResult, LoginFlow.SMART_CODE_REAUTH);
            }
        }
        else {
            return generateTokenResult;
        }
    }
    async authenticateByClearIAL2(authCode, redirectUri, organizationId, useFallback) {
        const generateTokenResult = await this.authService.generateTokenByClearGFIDWithIAL2(authCode, redirectUri, organizationId, undefined, useFallback);
        return await this.handleTokenResult(generateTokenResult, LoginFlow.GFID_CLEAR_IAL2);
    }
    async reauthenticateByClearIAL2(authCode, redirectUri, organizationId) {
        if (!this.id)
            return err("INCORRECT_CREDENTIALS");
        const generateTokenResult = await this.authService.generateTokenByClearGFIDWithIAL2(authCode, redirectUri, organizationId);
        if (isOk(generateTokenResult)) {
            const user = generateTokenResult.user;
            if (user.type === UserType.PATIENT && user.organizations.some((org) => org.userId === this.id)) {
                return await this.handleTokenResult(generateTokenResult, LoginFlow.GFID_CLEAR_IAL2);
            }
            return err("INCORRECT_CREDENTIALS");
        }
        else {
            return generateTokenResult;
        }
    }
    async invalidateIAL2Token() {
        if (this.tokenInfo && this.tokenInfo.type === UserType.PATIENT) {
            await this.setTokenInfo({
                ...this.tokenInfo,
            });
        }
    }
    async authenticateByPatientId(patientId, organizationName) {
        const generateTokenResult = await this.authService.generateTokenByPatientId(this, patientId, organizationName);
        return await this.handleTokenResult(generateTokenResult, LoginFlow.BOT_LOGIN);
    }
    async authenticateByPatientIdWithIAL2ForTesting(authenticationProvider, patientId, organizationId) {
        const generateTokenResult = await this.authService.generateTokenByPatientIdWithIAL2ForTesting(authenticationProvider, patientId, organizationId);
        return await this.handleTokenResult(generateTokenResult, LoginFlow.GFID_CLEAR_IAL2);
    }
    async handleSAMLLogin(token) {
        const generateTokenResult = await this.authService.generateTokenByExistingToken(token);
        return await this.handleTokenResult(generateTokenResult, LoginFlow.SSO);
    }
    async authenticateByToken(token) {
        return await this.authService.getAuthenticatedUserWithDetails({ token });
    }
    async authenticateByShortLivedToken(token) {
        const generateTokenResult = await this.authService.generateTokenFromShortLivedToken(token);
        await this.handleTokenResult(generateTokenResult, null);
    }
    async resetPassword(newPassword, token) {
        await this.authService.resetPassword(newPassword, token);
    }
    async sendPasswordResetUrl(email) {
        await this.authService.sendPasswordResetEmail(email);
    }
    async changePassword(newPassword, currentPassword) {
        if (!this.email)
            return false;
        return await this.authService.changePassword(this.email, newPassword, currentPassword);
    }
    async changePasswordWithEmail(email, newPassword, currentPassword) {
        return await this.authService.changePassword(email, newPassword, currentPassword);
    }
    async authenticateScreenshotUser(generateTokenResult) {
        if (generateTokenResult.user.type !== UserType.SCREENSHOT_USER) {
            throw new Error("This API should only be used for screenshot users");
        }
        await this.handleTokenResult(generateTokenResult, LoginFlow.BOT_LOGIN);
    }
    async handleTokenResult(generateTokenResult, loginFlow) {
        if (isOk(generateTokenResult)) {
            const { token, expiration, user } = generateTokenResult;
            await this.updateTokenInfoFromTokenAndUser(token, expiration, user, loginFlow, null);
            return voidResult();
        }
        else {
            return generateTokenResult;
        }
    }
    async confirmPendingPatientOrganization(organizationId) {
        if (!this.tokenPendingPatientOrganizationSelection) {
            console.warn(`Trying to confirm healthcare system, but none are pending`);
            return;
        }
        const { token, expiration, user } = this.tokenPendingPatientOrganizationSelection;
        await this.updateTokenInfoFromTokenAndUser(token, expiration, user, LoginFlow.GFID_CLEAR, organizationId);
    }
    async updateTokenInfoFromTokenAndUser(token, expiration, user, loginFlow, selectedPatientOrganizationId) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const { firstName, lastName } = user;
        let providerId = null;
        if ("providerId" in user) {
            providerId = user.providerId;
        }
        if (user.type === UserType.PATIENT) {
            let chosenPatientOrganizationId = selectedPatientOrganizationId;
            if (!chosenPatientOrganizationId) {
                if (user.organizations.length === 1) {
                    console.log(`[GFID] Single healthcare organization for a patient, logging in`);
                    chosenPatientOrganizationId = user.organizations[0].organizationId;
                }
                else {
                    this.tokenPendingPatientOrganizationSelection = { token, expiration, user };
                    console.log(`[GFID] Multiple healthcare organizations for a patient, login pending user selection`);
                    return;
                }
            }
            this.tokenPendingPatientOrganizationSelection = undefined;
            let orgToUse;
            for (const healthcareSystem of user.organizations) {
                if (healthcareSystem.organizationId === chosenPatientOrganizationId) {
                    orgToUse = healthcareSystem;
                }
            }
            if (!orgToUse) {
                console.error(`No selected organization in token`);
                return;
            }
            const healthcareOrgToUse = checkUserType(orgToUse, UserType.HEALTHCARE_ORGANIZATION);
            const organizationName = orgToUse.name;
            const organizationLogoUrl = orgToUse.logoUrl;
            const organizationState = (_a = orgToUse === null || orgToUse === void 0 ? void 0 : orgToUse.state) !== null && _a !== void 0 ? _a : null;
            const organizationPrimaryContactName = (_b = healthcareOrgToUse === null || healthcareOrgToUse === void 0 ? void 0 : healthcareOrgToUse.primaryContactName) !== null && _b !== void 0 ? _b : "";
            const organizationEmail = (_c = healthcareOrgToUse === null || healthcareOrgToUse === void 0 ? void 0 : healthcareOrgToUse.primaryContactEmail) !== null && _c !== void 0 ? _c : "";
            const organizationPhone = (_d = healthcareOrgToUse === null || healthcareOrgToUse === void 0 ? void 0 : healthcareOrgToUse.primaryContactPhone) !== null && _d !== void 0 ? _d : null;
            const organizationPatientPortalLink = (_e = healthcareOrgToUse === null || healthcareOrgToUse === void 0 ? void 0 : healthcareOrgToUse.patientPortalLink) !== null && _e !== void 0 ? _e : null;
            const organizationPatientPortalName = (_f = healthcareOrgToUse === null || healthcareOrgToUse === void 0 ? void 0 : healthcareOrgToUse.patientPortalName) !== null && _f !== void 0 ? _f : null;
            const patientOrganizationType = orgToUse.type;
            let { phone: patientPhone, email: patientEmail, age: patientAge, proxyAccess, authenticatedWithIAL2 } = user;
            if (this.tokenInfo && this.tokenInfo.type === "PATIENT") {
                patientPhone = patientPhone !== null && patientPhone !== void 0 ? patientPhone : this.tokenInfo.phone;
                patientEmail = patientEmail !== null && patientEmail !== void 0 ? patientEmail : this.tokenInfo.email;
                patientAge = patientAge !== null && patientAge !== void 0 ? patientAge : this.tokenInfo.age;
                proxyAccess = proxyAccess !== null && proxyAccess !== void 0 ? proxyAccess : this.tokenInfo.proxyAccess;
                authenticatedWithIAL2 = authenticatedWithIAL2 !== null && authenticatedWithIAL2 !== void 0 ? authenticatedWithIAL2 : this.tokenInfo.authenticatedWithIAL2;
            }
            const tokenInfo = {
                type: user.type,
                token,
                expiration,
                firstName,
                lastName,
                phone: patientPhone,
                email: patientEmail,
                state: user.state,
                age: patientAge,
                organizationName,
                organizationLogoUrl,
                organizationState,
                onboardingComplete: null,
                organizationPrimaryContactName,
                organizationEmail,
                organizationPhone,
                organizationPatientPortalLink,
                organizationPatientPortalName,
                createdAt: user.createdAt,
                patientOrganizationType,
                gfidProvider: user.gfidProvider,
                proxyAccess,
                selectedPatientOrganizationId: chosenPatientOrganizationId,
                providerId: null,
                authenticatedWithIAL2,
            };
            await this.setIdentifierForAnalytics(user, loginFlow);
            await this.setTokenInfo(tokenInfo);
        }
        else if (user.type === "SCREENSHOT_USER") {
            const { name: organizationName, logoUrl: organizationLogoUrl } = user.organization;
            const tokenInfo = {
                ...{
                    type: user.type,
                    token,
                    expiration,
                    firstName,
                    lastName,
                    organizationName,
                    organizationLogoUrl,
                    organizationState: null,
                    onboardingComplete: null,
                    providerId: null,
                },
                ...(user.organization.type === UserType.HEALTHCARE_ORGANIZATION
                    ? {
                        organizationPrimaryContactName: user.organization.primaryContactName,
                        organizationEmail: user.organization.primaryContactEmail,
                        organizationPhone: user.organization.primaryContactPhone,
                        organizationPatientPortalLink: user.organization.patientPortalLink,
                        organizationPatientPortalName: user.organization.patientPortalName,
                    }
                    : {
                        organizationPrimaryContactName: "",
                        organizationEmail: "",
                        organizationPhone: null,
                        organizationPatientPortalName: null,
                    }),
            };
            await this.setIdentifierForAnalytics(user, loginFlow);
            await this.setTokenInfo(tokenInfo);
        }
        else {
            const { name: organizationName, logoUrl: organizationLogoUrl } = user.organization;
            const { email, avatarUrl, phone, title, passwordUpdatedAt } = user;
            const onChainAddress = user.onChainAddress;
            const organizationState = (_g = user.organization.state) !== null && _g !== void 0 ? _g : null;
            const onboardingComplete = user.onboardingComplete;
            const tokenInfo = {
                type: user.type,
                token,
                expiration,
                firstName,
                lastName,
                email,
                avatarUrl,
                providerId,
                organizationName,
                organizationLogoUrl,
                organizationState,
                onChainAddress,
                onboardingComplete,
                phone,
                title,
                passwordUpdatedAt,
                organizationIsLive: user.organization.isLive,
                ...(user.type === UserType.HEALTHCARE_ORGANIZATION ? { associatedPatientMRN: (_h = user.associatedPatient) === null || _h === void 0 ? void 0 : _h.mrn } : {}),
            };
            await this.setIdentifierForAnalytics(user, loginFlow);
            await this.setTokenInfo(tokenInfo);
        }
    }
    async refreshTokenInfo() {
        if (!this.tokenInfo)
            return;
        const user = await this.authService.getAuthenticatedUserWithDetails(this);
        const selectedPatientOrganizationId = this.tokenInfo.type === UserType.PATIENT ? this.tokenInfo.selectedPatientOrganizationId : null;
        if (user.type === UserType.HEALTHCARE_ORGANIZATION && this.associatedPatientMRN) {
            user.associatedPatient = { mrn: this.associatedPatientMRN };
        }
        await this.updateTokenInfoFromTokenAndUser(this.tokenInfo.token, this.tokenInfo.expiration, user, null, selectedPatientOrganizationId);
    }
    async regenerateToken() {
        if (!this.token)
            return;
        const generateTokenResult = await this.authService.generateTokenByExistingToken(this.token);
        await this.handleTokenResult(generateTokenResult, null);
    }
    logout() {
        void this.setTokenInfo(null);
    }
    async getShortLivedTokenForAppLaunch() {
        if (!this.isProvider)
            throw new Error("Wrong user type");
        if (!this.token)
            throw new Error("No token");
        return await this.authService.getShortLivedTokenForAppLaunch(this, this.token);
    }
    scheduleExpirationTimerIfNeeded(windowMS) {
        console.log(`scheduleExpirationTimerIfNeeded ${Date.now()}`);
        if (this.expirationTimer) {
            clearTimeout(this.expirationTimer);
        }
        if (this.tokenInfo) {
            const delay = this.tokenInfo.expiration * 1000 - Date.now();
            const handleTokenExpire = () => {
                console.log("token expired");
                void this.setTokenInfo(null);
            };
            if (delay <= 0) {
                handleTokenExpire();
            }
            else if (delay <= windowMS) {
                this.expirationTimer = setTimeout(handleTokenExpire, delay);
            }
        }
    }
    async setIdentifierForAnalytics(user, loginFlow) {
        try {
            heap.identify(user.id);
            if (loginFlow) {
                heap.addUserProperties({ LoginFlow: loginFlow.toString(), userType: user.type.toString() });
            }
            if (this.orgId) {
                heap.addEventProperties({ organizationId: this.orgId });
            }
        }
        catch (e) {
            console.log("heap exception", e);
        }
    }
    async setTokenInfo(tokenInfo) {
        if (tokenInfo !== null && top !== self && !this.embeddedManager.isSmartLaunch && !this.embeddedManager.embedded) {
            console.error("Invalid user embed");
            this.invalidEmbed = true;
            return;
        }
        if (!this.embeddedManager.embedded) {
            this.persistence.setTokenInfo(tokenInfo);
        }
        this.tokenInfo = tokenInfo;
        if (tokenInfo === null) {
            this.tokenPendingPatientOrganizationSelection = undefined;
            try {
                heap.removeEventProperty("organizationId");
            }
            catch (e) {
                console.log("heap exception", e);
            }
        }
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], AuthManager.prototype, "tokenInfo", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], AuthManager.prototype, "invalidEmbed", void 0);
__decorate([
    observable,
    __metadata("design:type", GenerateUserTokenResult)
], AuthManager.prototype, "tokenPendingPatientOrganizationSelection", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "token", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "isLoggedIn", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "tokenPayload", null);
__decorate([
    computed,
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "permissions", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "isProvider", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "isEmbeddedProvider", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "userType", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "selectedOrganization", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "id", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "mrn", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "orgId", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "name", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "firstName", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "lastName", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "providerId", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "state", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "createdAt", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "email", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "phone", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "formattedPhone", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "title", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "age", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "isUnderage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "avatarUrl", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "orgLogoUrl", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "orgName", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "orgState", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "onChainAddress", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "orgPrimaryContactName", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "orgEmail", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "orgPhone", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "orgPatientPortalLink", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "orgPatientPortalName", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "patientOrgType", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "isUserProfileEmpty", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "isDataUser", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "passwordUpdatedAt", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "organizationIsLive", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "organizationIsDTC", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "isOrgAdmin", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "isResearcher", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "availablePatientOrganizations", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "gfidProvider", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "proxyAccess", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "authenticatedWithIAL2", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "associatedPatientMRN", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AuthManager.prototype, "consentingPatientInfo", null);
